/**
 * Recipe Form Page
 *
 * Create and edit recipes with all fields including photo upload,
 * tags, ingredients, and instructions.
 */

import { Head, router, useForm } from '@inertiajs/react';
import type { RecipeFormProps } from '@/types/food';
import { Navbar } from '@/components';
import { PhotoUpload } from '@/components/food/PhotoUpload';
import { TagSelector } from '@/components/food/TagSelector';
import { IngredientFormList } from '@/components/food/IngredientFormList';
import type { RecipeIngredientFormData } from '@/components/food/IngredientRow';

/**
 * Form data structure matching Rails nested attributes
 */
interface RecipeFormData {
  title: string;
  description: string;
  photo: File | null;
  preparation_time_minutes: number | null;
  cooking_time_minutes: number;
  servings: number;
  calories_per_portion: number | null;
  healthiness_rating: number | null;
  eat_immediately: boolean;
  fridge_life_days: number | null;
  freezable: boolean;
  tag_ids: number[];
  recipe_ingredients_attributes: RecipeIngredientFormData[];
  instructions: string;
}

export default function Form({
  recipe,
  ingredients,
  tags,
}: RecipeFormProps) {
  const isEditing = recipe !== null;

  // Initialize form with existing data or defaults
  const { data, setData, post, patch, processing, errors } = useForm<RecipeFormData>({
    title: recipe?.title || '',
    description: recipe?.description || '',
    photo: null,
    preparation_time_minutes: recipe?.preparationTimeMinutes ?? null,
    cooking_time_minutes: recipe?.cookingTimeMinutes || 0,
    servings: recipe?.servings || 4,
    calories_per_portion: recipe?.caloriesPerPortion ?? null,
    healthiness_rating: recipe?.healthinessRating ?? null,
    eat_immediately: recipe?.eatImmediately || false,
    fridge_life_days: recipe?.fridgeLifeDays ?? null,
    freezable: recipe?.freezable || false,
    tag_ids: recipe?.tags.map((t) => t.id) || [],
    recipe_ingredients_attributes:
      recipe?.ingredients.map((ing, index) => ({
        id: ing.id,
        ingredient_id: ing.ingredientId,
        quantity: ing.quantity ?? null,
        unit: ing.unit || '',
        notes: ing.notes || '',
        optional: ing.optional,
        position: index + 1,
      })) || [],
    instructions: recipe?.instructions || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditing) {
      patch(`/food/recipes/${recipe.id}`, {
        forceFormData: true,
      });
    } else {
      post('/food/recipes', {
        forceFormData: true,
      });
    }
  };

  const handleCancel = () => {
    if (isEditing) {
      router.visit(`/food/recipes/${recipe.id}`);
    } else {
      router.visit('/food/recipes');
    }
  };

  const handleAddTag = (tagName: string) => {
    // Create new tag via API and add to selection
    fetch('/food/tags', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
      },
      body: JSON.stringify({ tag: { name: tagName } }),
    })
      .then((res) => res.json())
      .then((newTag) => {
        if (newTag.id) {
          setData('tag_ids', [...data.tag_ids, newTag.id]);
          // Note: The tag will appear in the selector on next page load
        }
      })
      .catch(console.error);
  };

  return (
    <>
      <Head title={isEditing ? `Edit ${recipe.title}` : 'New Recipe'} />
      <div className="min-h-screen bg-gray-50">
        <Navbar currentSection="food" />
        <div className="max-w-[900px] mx-auto px-5 py-5">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">
            {isEditing ? 'Edit Recipe' : 'Add New Recipe'}
          </h1>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="recipe-form"
              disabled={processing}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-blue-600 border border-blue-600 rounded-lg text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {processing ? 'Saving...' : 'Save Recipe'}
            </button>
          </div>
        </div>

        <form id="recipe-form" onSubmit={handleSubmit}>
          {/* Basic Information */}
          <section className="bg-white rounded-xl p-6 mb-5 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200">
              Basic Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {/* Title */}
              <div className="col-span-2 flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Recipe Title
                </label>
                <input
                  type="text"
                  value={data.title}
                  onChange={(e) => setData('title', e.target.value)}
                  placeholder="e.g., Spaghetti Carbonara"
                  className="px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                  required
                />
                {errors.title && (
                  <span className="text-xs text-red-600">{errors.title}</span>
                )}
              </div>

              {/* Description */}
              <div className="col-span-2 flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Description <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={data.description}
                  onChange={(e) => setData('description', e.target.value)}
                  placeholder="A brief description of the dish..."
                  rows={3}
                  className="px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm resize-y min-h-[100px] focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                />
              </div>

              {/* Photo */}
              <div className="col-span-2 flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">Photo</label>
                <PhotoUpload
                  value={data.photo}
                  existingUrl={recipe?.photoUrl || null}
                  onChange={(file) => setData('photo', file)}
                />
              </div>
            </div>
          </section>

          {/* Time & Servings */}
          <section className="bg-white rounded-xl p-6 mb-5 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200">
              Time & Servings
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {/* Prep time */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Preparation Time <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={data.preparation_time_minutes ?? ''}
                    onChange={(e) =>
                      setData(
                        'preparation_time_minutes',
                        e.target.value ? Number(e.target.value) : null
                      )
                    }
                    placeholder="0"
                    min="0"
                    className="w-20 px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                  />
                  <span className="text-sm text-gray-600">minutes</span>
                </div>
              </div>

              {/* Cooking time */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Cooking Time
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={data.cooking_time_minutes || ''}
                    onChange={(e) =>
                      setData('cooking_time_minutes', Number(e.target.value) || 0)
                    }
                    placeholder="30"
                    min="0"
                    className="w-20 px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                    required
                  />
                  <span className="text-sm text-gray-600">minutes</span>
                </div>
                {errors.cooking_time_minutes && (
                  <span className="text-xs text-red-600">
                    {errors.cooking_time_minutes}
                  </span>
                )}
              </div>

              {/* Servings */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">Servings</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={data.servings}
                    onChange={(e) => setData('servings', Number(e.target.value) || 1)}
                    placeholder="4"
                    min="1"
                    className="w-20 px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                    required
                  />
                  <span className="text-sm text-gray-600">portions</span>
                </div>
              </div>

              {/* Calories */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Calories per Serving{' '}
                  <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={data.calories_per_portion ?? ''}
                    onChange={(e) =>
                      setData(
                        'calories_per_portion',
                        e.target.value ? Number(e.target.value) : null
                      )
                    }
                    placeholder="450"
                    min="0"
                    className="w-20 px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                  />
                  <span className="text-sm text-gray-600">kcal</span>
                </div>
              </div>
            </div>
          </section>

          {/* Storage Information */}
          <section className="bg-white rounded-xl p-6 mb-5 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200">
              Storage Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {/* Toggles */}
              <div className="col-span-2 flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={data.eat_immediately}
                    onChange={(e) => setData('eat_immediately', e.target.checked)}
                    className="w-[18px] h-[18px] rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Eat immediately after preparation
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={data.freezable}
                    onChange={(e) => setData('freezable', e.target.checked)}
                    className="w-[18px] h-[18px] rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Can be frozen</span>
                </label>
              </div>

              {/* Fridge life */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Fridge Life <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={data.fridge_life_days ?? ''}
                    onChange={(e) =>
                      setData(
                        'fridge_life_days',
                        e.target.value ? Number(e.target.value) : null
                      )
                    }
                    placeholder="3"
                    min="0"
                    className="w-20 px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                  />
                  <span className="text-sm text-gray-600">days</span>
                </div>
              </div>

              {/* Healthiness rating */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Healthiness Rating{' '}
                  <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() =>
                        setData(
                          'healthiness_rating',
                          data.healthiness_rating === rating ? null : rating
                        )
                      }
                      className={`w-10 h-10 border-2 rounded-lg flex items-center justify-center text-lg font-medium transition-colors ${
                        data.healthiness_rating === rating
                          ? 'border-amber-400 bg-amber-100 text-amber-700'
                          : 'border-gray-200 hover:border-amber-400 hover:bg-amber-50'
                      }`}
                    >
                      {rating}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Tags */}
          <section className="bg-white rounded-xl p-6 mb-5 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200">
              Tags
            </h2>
            <TagSelector
              tags={tags}
              selectedTagIds={data.tag_ids}
              onChange={(tagIds) => setData('tag_ids', tagIds)}
              onAddTag={handleAddTag}
            />
          </section>

          {/* Ingredients */}
          <section className="bg-white rounded-xl p-6 mb-5 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200">
              Ingredients
            </h2>
            <IngredientFormList
              ingredients={data.recipe_ingredients_attributes}
              availableIngredients={ingredients}
              onChange={(ings) => setData('recipe_ingredients_attributes', ings)}
            />
          </section>

          {/* Instructions */}
          <section className="bg-white rounded-xl p-6 mb-5 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200">
              Instructions
            </h2>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">
                Cooking Steps
              </label>
              <p className="text-xs text-gray-400 mb-2">
                Enter each step on a new line. They will be numbered automatically.
              </p>
              <textarea
                value={data.instructions}
                onChange={(e) => setData('instructions', e.target.value)}
                placeholder="1. First step...&#10;2. Second step...&#10;3. Third step..."
                rows={8}
                className="px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm resize-y min-h-[200px] focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
              />
            </div>
          </section>

          {/* Footer */}
          <div className="flex justify-end gap-3 py-5">
            <button
              type="button"
              onClick={handleCancel}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-blue-600 border border-blue-600 rounded-lg text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {processing ? 'Saving...' : 'Save Recipe'}
            </button>
          </div>
        </form>
        </div>
      </div>
    </>
  );
}
